import { z } from '@customerio/hydra-validation';
import type {
  FacebookLeadAdsCredential as UIAPIFacebookLeadAdsCredential,
  FacebookLeadAdsForm as UIAPIFacebookLeadAdsForm,
} from '@customerio/hydra-ui-api';
import { defineEntity, readSchema } from '../shared/define-entity';
import type { Segment } from '../segment/entity';
import { hasDuplicateTrimmedValue } from '../shared/validation';

/**
 * Facebook Lead Ads credential + forms (workspace-scoped). The integrations
 * directory reads credential presence (Facebook Lead Ads "enabled") and the
 * count of connected forms (contributes to the Forms card's total).
 */
export type FacebookLeadAdsCredential = UIAPIFacebookLeadAdsCredential;
export type FacebookLeadAdsForm = UIAPIFacebookLeadAdsForm;

const FacebookLeadAdsCredentialFromApi = readSchema<FacebookLeadAdsCredential>(z.looseObject({ page_id: z.string() }));

const FacebookLeadAdsFormFromApi = readSchema<FacebookLeadAdsForm>(
  z.looseObject({
    id: z.string(),
  }),
);

/** @public */
export const facebookLeadAdsForm = defineEntity<FacebookLeadAdsForm, FacebookLeadAdsForm, void, undefined, 'workspace'>(
  {
    name: 'facebook_lead_ads_form',
    scope: 'workspace',
    pk: (f) => f.id,
    schema: { read: FacebookLeadAdsFormFromApi },
  },
);

export const facebookLeadAdsFormKeys = facebookLeadAdsForm.keys;

/** Credential is a per-workspace singleton — keyed under the form entity's namespace. */
export const facebookLeadAdsCredentialKey = (workspaceId: number) =>
  [...facebookLeadAdsForm.keys.all(workspaceId), 'credential'] as const;

export function parseFacebookLeadAdsCredential(raw: UIAPIFacebookLeadAdsCredential): FacebookLeadAdsCredential {
  return FacebookLeadAdsCredentialFromApi.parse(raw);
}

export function parseFacebookLeadAdsForm(raw: UIAPIFacebookLeadAdsForm): FacebookLeadAdsForm {
  return facebookLeadAdsForm.parse(raw);
}

/**
 * The statuses a form carries, as the API spells them.
 *
 * `active` is a form taking leads, `inactive` one Facebook has but this
 * workspace is not listening to, and `archived` one archived in Facebook. The
 * API speaks of connecting a form, but no form is ever `connected`: connecting
 * one is setting it `active`.
 */
export const FACEBOOK_FORM_STATUS = {
  active: 'active',
  inactive: 'inactive',
  archived: 'archived',
} as const;

/** A form that is actively connected: its leads reach the workspace. */
export function isConnectedFacebookForm(form: Pick<FacebookLeadAdsForm, 'status'>): boolean {
  return form.status === FACEBOOK_FORM_STATUS.active;
}

export function isArchivedFacebookForm(form: Pick<FacebookLeadAdsForm, 'status'>): boolean {
  return form.status === FACEBOOK_FORM_STATUS.archived;
}

/**
 * A form that can be connected now: one Facebook still has that this
 * workspace is not listening to. An archived form is not offered, because
 * Facebook sends it no leads.
 */
export function isConnectableFacebookForm(form: Pick<FacebookLeadAdsForm, 'status'>): boolean {
  return form.status === FACEBOOK_FORM_STATUS.inactive;
}

export function isDisconnectedFacebookForm(form: Pick<FacebookLeadAdsForm, 'status'>): boolean {
  return !isConnectedFacebookForm(form);
}

export function isOrWasConnectedFacebookForm(
  form: Pick<FacebookLeadAdsForm, 'status' | 'facebook_form_id'>,
  segments: readonly Pick<Segment, 'linked_form_id'>[],
): boolean {
  return (
    isConnectedFacebookForm(form) ||
    (isDisconnectedFacebookForm(form) && facebookLeadAdsFormSegment(form, segments) != null)
  );
}

export function facebookLeadAdsFormValue(form: Pick<FacebookLeadAdsForm, 'facebook_form_id'>): string {
  return form.facebook_form_id;
}

export function facebookLeadAdsFormLabel(form: Pick<FacebookLeadAdsForm, 'facebook_form_name'>): string {
  return facebookFormDisplayName(form.facebook_form_name);
}

export function facebookLeadAdsFormIdLowercase(form: Pick<FacebookLeadAdsForm, 'facebook_form_id'>): string {
  return form.facebook_form_id.toLowerCase();
}

export function hasDuplicateFacebookLeadAdsFormMapping(form: Pick<FacebookLeadAdsForm, 'attr_maps'>): boolean {
  return hasDuplicateTrimmedValue(form.attr_maps.map(({ cio_attr }) => cio_attr));
}

export function hasFacebookLeadAdsFormCioIdMapping(form: Pick<FacebookLeadAdsForm, 'attr_maps'>): boolean {
  return form.attr_maps.some(({ cio_attr }) => cio_attr === 'cio_id');
}

export function facebookLeadAdsFormSegment<T extends Pick<Segment, 'linked_form_id'>>(
  form: Pick<FacebookLeadAdsForm, 'facebook_form_id'>,
  segments: readonly T[],
): T | undefined {
  return segments.find((segment) => segment.linked_form_id === form.facebook_form_id);
}

/**
 * A Facebook form's name without the date the Graph API appends to it.
 *
 * Facebook names a form "Ami Co. form created on Thu Oct 22, 2020 10:04am";
 * the date is noise wherever the form is named. Only the last occurrence is
 * cut, so a form actually named "... created on ..." keeps the part that is
 * its name.
 */
export function facebookFormDisplayName(name: string): string {
  const parts = name.split('created on');
  if (parts.length > 1) {
    parts.pop();
  }
  return parts.join('created on').trim();
}
